import { writeCrossEntityActions } from '../entityActions'
import { AsyncOperationError, UpdateConflictError } from '../errors'
import type { IEntityActionsPayload } from '~/cynqueModules/entityActions'

const mn = '[services/crud/writeEntityActions]'

/**
 * Send a multi-action write to `POST /api/transactions`.
 *
 * There is one route for every payload: each action's own `entity` says which
 * list its rows belong to. The per-collection `/api/<silo>msservices/actions`
 * routes, and the `entityName` switch that chose between them and this one,
 * were retired by cynque-api-server #1627 phase B.
 */
export async function writeEntityActions(payload: IEntityActionsPayload) {
  const fn = `${mn} writeEntityActions |`
  try {
    return await writeCrossEntityActions(payload)
  } catch (e) {
    throw _asUpdateConflictIfItIsOne(fn, e)
  }
}

/**
 * ⚠ A CONFLICT MUST NOT ARRIVE AS A GENERIC OPERATION FAILURE.
 *
 * The endpoint is async, so the server validates versions before enqueueing and
 * a stale one comes back as a synchronous 409, which `httpPost` already turns
 * into an `UpdateConflictError`. But a real server may also discover a conflict
 * once the operation is running, and `pollOperation` reports every failed
 * operation as an `AsyncOperationError` carrying the server's own `errorType`
 * string. That type is invisible to everything the app has built around
 * conflicts.
 *
 * So a failure typed `Conflict` is re-thrown as the error the app recognises.
 * Everything else passes through untouched — an `AsyncOperationError` for a
 * genuine operation failure is still the right error.
 *
 * ⚠ This does NOT reactivate `_updateEntityRetryingOneConflict`: that guards
 * `patchEntityData` and rethrows for arrays anyway, so it has never covered a
 * multi-row write. What it restores is the CLASSIFICATION — which is what a
 * user-facing conflict message would key off, and `saveErrorNotification` has no
 * such case today.
 */
function _asUpdateConflictIfItIsOne(fn: string, e: unknown): unknown {
  if (e instanceof AsyncOperationError && e.errorType === 'Conflict') {
    console.warn(
      `${fn} the transaction failed with a Conflict.` +
        ` Re-throwing as UpdateConflictError so it is handled as one.`
    )
    return new UpdateConflictError('POST', '/api/transactions', e.details)
  }
  return e
}
